import moment from 'moment';

import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel';
import ScoolUserInviteModel from '../models/ScoolUserInviteModel';
import CounselorGroupSessionModel from '../models/CounselorGroupSessionModel';
import AdminViewCounselorModel from '../models/AdminViewCounselorModel';
import AdminViewStudentModel from '../models/AdminViewStudentModel';
import CollegeModel from '../models/CollegeModel';
import CollegeReportModel from '../models/CollegeReportModel';
import AppointmentModel from '../models/AppointmentModel';
import ZoomTimelineModel from '../models/ZoomTimelineModel';
import UserModel from '../models/UserModel';
import DemographicsModel from '../models/DemographicsModel';
import ByStateModel from '../models/ByStateModel';
import SpecialtyModel from '../models/SpecialtyModel';
import ByStateDateModel from '../models/ByStateDateModel';
import ByGenderModel from '../models/ByGenderModel';
import StateAvailableHoursModel from '../models/StateAvailableHoursModel';

export default class AdminClient extends BaseAPIClient {
  sendStudentInvite(schoolId, email, first_name, last_name) {
    return this._addSchoolUserInvite(
      schoolId,
      email,
      'student',
      first_name,
      last_name
    );
  }
  sendAdminInvite(schoolId, email, first_name, last_name) {
    return this._addSchoolUserInvite(
      schoolId,
      email,
      'admin',
      first_name,
      last_name
    );
  }
  _addSchoolUserInvite(schoolId, email, role, first_name, last_name) {
    return this._post(`users/schools/${schoolId}/invites/`, {
      email,
      role,
      first_name,
      last_name
    }).then((m) => new ScoolUserInviteModel(m));
  }

  sendMessage(school, recipientType, heading, message, message_thread) {
    const data = {
      heading,
      message,
      school,
      counselor_type: 'on_campus'
    };

    // todo: support for counselor_type = 'third_party' and user_type = 'admin' with school = null

    if (recipientType && recipientType === 'counselors') {
      data.user_type = 'counselor';
    } else if (recipientType && recipientType === 'students') {
      data.user_type = 'student';
    }

    if (message_thread) {
      data.message_thread = message_thread;
    }

    return this._post('messages/send/', data);
  }

  submitCounselorSessionRequest(user_id, counselor_id, start_time, end_time) {
    return this._post('counseling_sessions/admin/appointment_requests/', {
      user_id,
      counselor_id,
      start_time,
      end_time
    });
  }

  getOnCampusCounselors(
    schoolId,
    filters,
    limit = 100,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true
  ) {
    filters.limit = limit;
    filters.offset = offset;

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(
      `counselors/schools/${schoolId}/counselors_v2/`,
      filters
    ).then((m) => new ListResponseModel(m, AdminViewCounselorModel));
  }

  getAllCounselors(
    filters = {},
    limit = 100,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true
  ) {
    const data = {
      search: filters.search ? filters.search : undefined,
      active: filters.active === null ? undefined : filters.active,
      state_ids: filters.stateId > 0 ? filters.stateId : undefined,
      limit,
      offset,
      ordering: '-verified'
    };

    if (sortedColumn) {
      data.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get('counselors/all_counselors_v2/', data).then(
      (m) => new ListResponseModel(m, AdminViewCounselorModel)
    );
  }

  async getAllCounselorsForAdminReport(search, isActive, isVerified, hasAvailability, stateId, languageId, pageToken, count = 20) {
    return this._get('v2/counselors/accounts/', {
            search,
            isActive,
            isVerified,
            hasAvailability,
            stateId,
            languageId,
            pageToken,
            count,
          });
  }

  getAllCounselorsForStudent(
    studentUserId,
    search = '',
    limit = 100,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true
  ) {
    const filters = {
      search,
      limit,
      offset,
      ordering: '-verified'
    };

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(
      `counselors/admin/counselors_for_user/${studentUserId}/`,
      filters
    ).then((m) => new ListResponseModel(m, AdminViewCounselorModel));
  }

  getCounselorDemographics() {
    return this._get(`counselors/counselor_demographics`).then(
      (m) => new ListResponseModel(m, DemographicsModel)
    );
  }

  getStudentDemographics() {
    return this._get(`users/student_demographics`).then(
      (m) => new ListResponseModel(m, DemographicsModel)
    );
  }
  getCounselorByState() {
    return this._get(`counselors/counselor_states`).then(
      (m) => new ListResponseModel(m, ByStateModel)
    );
  }

  getCounselorGender() {
    return this._get(`counselors/counselor_gender`).then(
      (m) => new ListResponseModel(m, ByGenderModel)
    );
  }

  getSessionsByState() {
    return this._get(`counselors/session_states`).then(
      (m) => new ListResponseModel(m, ByStateDateModel)
    );
  }

  getStudentByState() {
    return this._get(`users/student_states`).then(
      (m) => new ListResponseModel(m, ByStateModel)
    );
  }

  getCounselorSpecialty() {
    return this._get(`counselors/counselor_specialty`).then(
      (m) => new ListResponseModel(m, SpecialtyModel)
    );
  }

  getAllUserList(search = '', limit = 10, offset = 0, is_counselor = false) {
    const params = {
      search,
      limit,
      offset
    };

    if (is_counselor) {
      params.is_counselor = is_counselor;
    }

    return this._get(`users/admin/all_users/`, params).then(
      (m) => new ListResponseModel(m, UserModel)
    );
  }

  getMyCollegeUserList(search = '', limit = 10, offset = 0) {
    return this._get(`users/college_admin/campus_users/`, {
      search,
      limit,
      offset
    }).then((m) => new ListResponseModel(m, UserModel));
  }

  getOnCampusCounselorsCSV(
    schoolId,
    filters,
    sortedColumn = '',
    sortedAsc = true
  ) {
    filters.format = 'csv';
    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(`counselors/schools/${schoolId}/counselors_v2/`, filters);
  }

  getAllCounselorsCSV(filters = {}, sortedColumn = '', sortedAsc = true) {
    const data = {
      search: filters.search ? filters.search : undefined,
      active: filters.active === null ? undefined : filters.active,
      state_ids: filters.stateId > 0 ? filters.stateId : undefined,
      format: 'csv',
      ordering: '-verified'
    };

    if (sortedColumn) {
      data.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get('counselors/all_counselors_v2/', data);
  }

  getGroupEvents(filters, limit = 10, offset = 0) {
    const data = filters || {};
    data.type = 'college_event';
    data.limit = limit;
    data.offset = offset;
    data.start_time_after = new Date().toISOString();

    return this._get(
      'counseling_sessions/counselors/group_sessions/',
      data
    ).then((m) => new ListResponseModel(m, CounselorGroupSessionModel));
  }

  verifyCounselor(id, verified = true) {
    return this._post(`counselors/counselors/${id}/verification/`, {
      verified
    });
  }

  getSchoolsList(
    filters,
    limit = 10,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true
  ) {
    filters.limit = limit;
    filters.offset = offset;

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(`schools/admin/schools_v2/`, filters).then(
      (m) => new ListResponseModel(m, CollegeModel)
    );
  }

  getSchoolsListCSV(filters, sortedColumn = '', sortedAsc = true) {
    filters.format = 'csv';

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(`schools/admin/schools_v2/`, filters);
  }

  activateCollegeAdmin(userId) {
    return this._post('v2/user/activate/', {
      userId
    });
  }
  deactivateCollegeAdmin(userId) {
    return this._post('v2/user/deactivate/', {
      userId
    });
  }
  getCollegeAdminList(searches, pageToken, recordCount) {
    return this._get('v2/campus-administration/campus-admins/list/admin/', {
      searches,
      pageToken,
      recordCount,
    });
  }

  getStudentsList(
    filters,
    limit = 10,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true
  ) {
    filters.limit = limit;
    filters.offset = offset;

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(`users/admin/students/`, filters).then(
      (m) => new ListResponseModel(m, AdminViewStudentModel)
    );
  }

  getStudentsListCSV(filters, sortedColumn = '', sortedAsc = true) {
    filters.format = 'csv';
    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }

    return this._get(`users/admin/students/`, filters);
  }

  getZoomTimeline(meeting_id) {
    return this._get(`zoom/meetings/${meeting_id}/timeline/`).then(
      (m) => new ZoomTimelineModel(m)
    );
  }

  getAppointmentsList(
    filters,
    limit = 10,
    offset = 0,
    sortedColumn = '',
    sortedAsc = true,
    showPending = false
  ) {
    filters.limit = limit;
    filters.offset = offset;
    filters.canceled = false;

    if (sortedColumn) {
      filters.ordering = !sortedAsc ? `-${sortedColumn}` : sortedColumn;
    }
    if (showPending) {
      filters.pending = true;
      return this._get(
        `counseling_sessions/admin/appointment_requests/`,
        filters
      ).then((m) => new ListResponseModel(m, AppointmentModel));
    } else {
      return this._get(
        `counseling_sessions/admin/counseling_sessions/`,
        filters
      ).then((m) => new ListResponseModel(m, AppointmentModel));
    }
  }

  getAppointmentsListCSV(filters) {
    filters.format = 'csv';
    return this._get(
      'counseling_sessions/admin/counseling_sessions_csv/',
      filters
    );
  }

  getSchool(schoolId) {
    return this._get(`schools/schools/${schoolId}/`).then(
      (s) => new CollegeModel(s)
    );
  }

  getSchoolReport(schoolId) {
    return this._get(`schools/schools/${schoolId}/report/`).then(
      (s) => new CollegeReportModel(s)
    );
  }

  getCollegeAdminCollegeDetailsReport(schoolId) {
    return this._get('v2/campus-administration/reports/college-details/', {
      schoolId
    });
  }

  getCollegeAdminStudentDetailsReport(
    schoolId,
    pageToken = undefined,
    search = undefined,
    customCampusTags = undefined
  ) {
    return this._get('v2/campus-administration/reports/student-details/', {
      schoolId,
      pageToken,
      search,
      customCampusTags
    });
  }

  getCollegeAdminSchoolReport(schoolId, fromDate = null, throughDate = null) {
    return this._get(`schools/schools/${schoolId}/report/`, {
      from_date: fromDate ? moment(fromDate).format('YYYY-MM-DD') : null,
      through_date: throughDate
        ? moment(throughDate).format('YYYY-MM-DD')
        : null
    }).then((s) => new CollegeReportModel(s));
  }

  getCollegeAdminSessionsListReport(
    schoolId, // : int
    from, // : moment.Moment?
    through, // : moment.Moment?
    includeStatus, // : string[]
    pageToken, // : string
    customCampusTags
  ) {
    return this._get('v2/campus-administration/reports/sessions/', {
      schoolId,
      from: from ? from.format('YYYY-MM-DDTHH:mm:ss') : null,
      through: through ? through.format('YYYY-MM-DDTHH:mm:ss') : null,
      includeStatus,
      pageToken,
      customCampusTags
    });
  }

  getCollegeAdminSessionsMetricsReport(
    schoolId, // : int
    from, // : moment.Moment?
    through, // : moment.Moment?
    includeStatus, // : string[]
    customCampusTags // : string[]
  ) {
    return this._get('v2/campus-administration/reports/sessions-metrics/', {
      schoolId,
      from: from ? from.format('YYYY-MM-DDTHH:mm:ss') : null,
      through: through ? through.format('YYYY-MM-DDTHH:mm:ss') : null,
      includeStatus,
      customCampusTags
    });
  }

  getCollegeAdminSessionsUniqueCounselors(
    schoolId, // : int
    from, // : moment.Moment?
    through, // : moment.Moment?
    includeStatus, // : string[]
    paidBy, // : string[]
    customCampusTags // : string[]
  ) {
    return this._get(
      'v2/campus-administration/reports/sessions/unique-counselors',
      {
        schoolId,
        from: from ? from.format('YYYY-MM-DDTHH:mm:ss') : null,
        through: through ? through.format('YYYY-MM-DDTHH:mm:ss') : null,
        includeStatus,
        paidBy,
        customCampusTags
      }
    );
  }

  getCollegeAdminSessionsUniqueStudents(
    schoolId, // : int
    from, // : moment.Moment?
    through, // : moment.Moment?
    includeStatus, // : string[]
    paidBy, // : string[]
    customCampusTags // : string[]
  ) {
    return this._get(
      'v2/campus-administration/reports/sessions/unique-students',
      {
        schoolId,
        from: from ? from.format('YYYY-MM-DDTHH:mm:ss') : null,
        through: through ? through.format('YYYY-MM-DDTHH:mm:ss') : null,
        includeStatus,
        paidBy,
        customCampusTags
      }
    );
  }



  getStudentCreditUsageCSV(schoolId, from, through) {
    return this._get('v2/campus-administration/exports/student-credit-usage/', {
      schoolId,
      from,
      through
    });
  }

  getCollegeAdminRegistrationsReport(
    schoolId,
    from = null,
    through = null,
    binBy = null,
    customCampusTags,
  ) {
    return this._get('v2/campus-administration/reports/registrations/', {
      schoolId,
      from: from ? moment(from).format('YYYY-MM-DD') : null,
      through: through ? moment(through).format('YYYY-MM-DD') : null,
      binBy,
      customCampusTags,
    });
  }

  getCollegeAdminScheduledSessionMetricsReport(schoolId) {
    return this._get(
      'v2/campus-administration/reports/scheduled-session-metrics/',
      {
        schoolId
      }
    );
  }

  getAdminSchoolReport() {
    return this._get(`schools/admin/report/`).then(
      (s) => new CollegeReportModel(s)
    );
  }

  editSchool(schoolId, data) {
    return this._patch(`schools/schools/${schoolId}/`, data).then(
      (s) => new CollegeModel(s)
    );
  }

  editAccount(id, _is_active) {
    return this._patch(`users/admin/accounts/${id}/`, {
      _is_active
    });
  }

  setDefaultIndividualCreditAllowance(schoolId, defaultIndividualCredits) {
    return this._post(`v2/schools/schools/${schoolId}/set-default-credits/`, {
      defaultIndividualCredits
    });
  }

  getStudentIndividualCreditsAvailable(schoolId, email) {
    return this._get(
      `v2/schools/schools/${schoolId}/users/available-individual-credits/`,
      {
        email
      }
    );
  }

  setStudentCreditAllowance(
    schoolId,
    studentUserId,
    individualCredits,
    groupCredits
  ) {
    return this._post(`v2/campus-administration/student-credit-allowance/`, {
      schoolId,
      studentUserId,
      individualCredits,
      groupCredits
    });
  }

  setStudentIndividualCreditAllowance(
    schoolId,
    studentUserId,
    individualCredits
  ) {
    return this._post(
      `v2/schools/school/${schoolId}/schoolusers/user/${studentUserId}/set-individual-credits/`,
      {
        individualCredits
      }
    );
  }

  requestCreditsForSchool(schoolId, message) {
    return this._post(`v2/schools/schools/${schoolId}/request-credits/`, {
      message
    });
  }

  validateEmailSchoolDomain(school_id, email) {
    return this._post('schools/schools/domain_email', {
      school_id,
      email
    }).then(
      (x) => !x.bad_email,
      () => false
    );
  }

  getAvailableHoursByState() {
    return this._get(`counselors/available_hours_by_states`).then(
      (m) => new ListResponseModel(m, StateAvailableHoursModel)
    );
  }

  async createCollegeAdmin(firstName, lastName, email, schoolId) {
    const response = await this._post(
      'v2/super-administration/college-admins/',
      {
        firstName,
        lastName,
        email,
        schoolId
      }
    );
    return {
      id: response.id,
      firstName: response.firstName,
      lastName: response.lastName,
      email: response.email,
      schoolId: response.schoolId,
      schoolName: response.schoolName,
      passwordResetToken: response.passwordResetToken
    };
  }

  inviteCounselor(firstName, lastName, email) {
    return this._post(
      'v2/super-administration/counselors/invite',
      {
        firstName,
        lastName,
        email
      }
    );
  }
  convertToCounselor(userId) {
    return this._post(
      'v2/super-administration/students/convert-to-counselor',
      {
        userId
      }
    );
  }

  getCounselorRating(counselorId) {
    return this._get('v2/counselors/rating/', { counselorId });
  }

  activateCounselor(counselorId) {
    return this._post('v2/super-administration/counselors/activate/', { counselorId })
  }
  deactivateCounselor(counselorId) {
    return this._post('v2/super-administration/counselors/deactivate/', { counselorId })
  }
  activateStudentUser(studentUserId) {
    return this._post('v2/super-administration/students/activate/', { studentUserId })
  }
  deactivateStudentUser(studentUserId) {
    return this._post('v2/super-administration/students/deactivate/', { studentUserId })
  }
  getFeedbackReport(searchCriteria, from, through, schoolId, pageToken) {
    return this._get('v2/counseling/feedback/admin/', { 
      searchCriteria,
      from, 
      through, 
      schoolId,
      pageToken,
    })
  }
  getAllCounselorsByLookup(searchCriteria, pageToken) {
    return this._get('v2/super-administration/counselors/lookup/', {searchCriteria, pageToken});
  }
}
